import Vue from "vue";
import VueRouter from "vue-router";
import store from "@/store";

Vue.use(VueRouter);

const routes = [
  {
    path: "/",
    redirect: "/auth"
  },
  {
    path: "/auth",
    redirect: "/auth/login",
    component: () =>
      import(/* webpackChunkName: "auth" */ "../views/Auth/index"),
    children: [
      {
        path: "login",
        name: "loginForm",
        // meta: { authorization: "100" },
        component: () =>
          import(
            /* webpackChunkName: "login-form" */ "../components/Auth/Login/LoginForm/index"
          )
      },
      {
        path: "login/change-password",
        name: "changePassword",
        component: () =>
          import(
            /* webpackChunkName: "change-password" */ "@/components/Auth/Login/ChangePassword"
          ),
        beforeEnter: (to, from, next) => {
          if (from.name === "loginForm" && localStorage.getItem("access_token"))
            next();
          else next({ name: "loginForm" });
        }
      },
      {
        path: "forget-password",
        name: "forgetPassword",
        component: () => import("@/components/Auth/ForgetPassword")
      },
      {
        path: "forget-password/email-verification",
        name: "emailVerification",
        component: () =>
          import("@/components/Auth/ForgetPassword/EmailVerification")
      },
      {
        path: "forget-password/reset-password",
        name: "resetPassword",
        component: () =>
          import("@/components/Auth/ForgetPassword/ResetPassword"),
        beforeEnter: (to, from, next) => {
          if (from.name === "emailVerification") next();
          else next({ name: "forgetPassword" });
        }
      }
    ]
  },
  {
    path: "/panel",
    name: "panel",
    redirect: `/panel/dashboard`,
    component: () => import("../views/Panel/Layout/index"),
    beforeEnter: async (to, from, next) => {
      if (localStorage.getItem("access_token")) {
        if (!store.getters["userInfo/GET_USERINFO"].userStatus) {
          await store.dispatch("userInfo/ACTION_USERINFO");
          // Since beforeEnter will be run after beforeEach, this following script must be checked to ensure authorization will be considered.
          // await AuthorizationCheck(to, from, next);
          next();
        } else {
          next();
        }
      } else next({ name: "loginForm" });
    },
    children: [
      {
        path: "dashboard",
        name: "panelDashboard",
        meta: { authorization: "100" },
        component: () => import("@/components/Panel/Dashboard")
      },
      {
        path: "transactions",
        name: "transactions",
        redirect: { name: "depositList" },
        component: () => import("@/views/Panel/Transactions"),
        children: [
          {
            path: "deposit",
            name: "depositList",
            meta: { authorization: "210" },
            component: () =>
              import("@/components/Panel/Transactions/Deposit/List")
          },
          {
            path: "deposit/:transactionId",
            redirect: { name: "depositList" }
          },
          {
            path: "deposit/:transactionId/details",
            name: "depositDetails",
            props: true,
            component: () =>
              import("@/components/Panel/Transactions/Deposit/Details")
          },

          {
            path: "withdrawal",
            name: "withdrawalList",
            meta: { authorization: "220" },
            component: () =>
              import("@/components/Panel/Transactions/Withdrawal/List")
          },
          {
            path: "withdrawal/:transactionId",
            redirect: { name: "withdrawalList" }
          },
          {
            path: "withdrawal/:transactionId/details",
            name: "withdrawalDetails",
            props: true,
            component: () =>
              import("@/components/Panel/Transactions/Withdrawal/Details")
          },

          {
            path: "refund",
            name: "refundList",
            meta: { authorization: "230" },
            component: () =>
              import("@/components/Panel/Transactions/Refund/List")
          },
          {
            path: "refund/:transactionId",
            redirect: { name: "refundList" }
          },
          {
            path: "refund/:transactionId/details",
            name: "refundDetails",
            props: true,
            component: () =>
              import("@/components/Panel/Transactions/Refund/Details")
          }
        ]
      },
      {
        path: "users",
        name: "users",
        meta: { authorization: "300" },
        component: () => import("@/components/Panel/Users/List")
      },
      {
        path: "users/:userId",
        redirect: { name: "users" }
      },
      {
        path: "users/:userId/details",
        name: "usersDetails",
        props: true,
        component: () => import("@/components/Panel/Users/Details")
      },
      {
        path: "businesses",
        name: "businesses",
        redirect: { name: "businessesList" },
        component: () => import("@/views/Panel/Businesses"),
        children: [
          {
            path: "businesses-list",
            name: "businessesList",
            component: () => import("@/components/Panel/Businesses/List")
          },
          {
            path: "businesses-list/:businessId",
            redirect: { name: "businessesList" }
          },
          {
            path: "businesses-list/:businessId/gateways",
            name: "gateways",
            props: true,
            component: () =>
              import("@/components/Panel/Businesses/Gateway/List")
          },
          {
            path: "businesses-list/:businessId/gateways/:gatewayId",
            redirect: { name: "gateways" }
          },
          {
            path: "businesses-list/:businessId/gateways/:gatewayId/details",
            name: "gatewaysDetails",
            props: true,
            component: () =>
              import("@/components/Panel/Businesses/Gateway/Details")
          },
          {
            path: "businesses-categories",
            name: "businessCategories",
            component: () =>
              import("@/components/Panel/Businesses/BusinessCategories")
          }
        ]
      },
      {
        path: "pos-management",
        name: "posManagement",
        redirect: { name: "posGatewaysList" },
        component: () => import("@/views/Panel/PosManagement"),
        children: [
          {
            path: "pos-gateways",
            name: "posGatewaysList",
            component: () => import("@/components/Panel/PosManagement/List")
          },
          {
            path: ":gatewayId",
            redirect: { name: "posGatewaysList" }
          },
          {
            path: ":gatewayId/currencies",
            name: "posGatewayCurrencies",
            props: true,
            component: () =>
              import("@/components/Panel/Businesses/Gateway/Details")
          }
        ]
      },
      {
        path: "currency-management",
        name: "currencyManagement",
        redirect: { name: "currencyManagementList" },
        component: () => import("@/views/Panel/CurrencyManagement"),
        children: [
          {
            path: "list",
            name: "currencyManagementList",
            component: () =>
              import("@/components/Panel/CurrencyManagement/List")
          },
          {
            path: "add-currency",
            name: "addCurrency",
            component: () =>
              import("@/components/Panel/CurrencyManagement/AddCurrency")
          },
          {
            path: ":currencyId",
            redirect: { name: "addCurrency" }
          },
          {
            path: ":currencyId/settings",
            name: "currencySettings",
            props: true,
            component: () =>
              import("@/components/Panel/CurrencyManagement/CurrencySettings")
          }
        ]
      },
      {
        path: "app-versioning",
        name: "appVersioning",
        component: () => import("@/components/Panel/AppVersioning")
      },
      {
        path: "admin-management",
        name: "adminManagement",
        redirect: { name: "roles" },
        component: () => import("@/views/Panel/AdminManagement"),
        children: [
          {
            path: "roles",
            name: "roles",
            component: () =>
              import("@/components/Panel/AdminManagement/Roles/List")
          },
          {
            path: "roles/add-role",
            name: "addRole",
            props: true,
            component: () =>
              import("@/components/Panel/AdminManagement/Roles/Actions/Add")
          },
          {
            path: "roles/:roleName",
            redirect: { name: "roles" }
          },
          {
            path: "roles/:roleName/edit-actions",
            name: "editRoleActions",
            props: true,
            component: () =>
              import("@/components/Panel/AdminManagement/Roles/Actions/Edit")
          },
          {
            path: "invite-admin",
            name: "inviteAdmin",
            component: () =>
              import("@/components/Panel/AdminManagement/InviteAdmin")
          },
          {
            path: "admin-list",
            name: "adminList",
            component: () =>
              import("@/components/Panel/AdminManagement/AdminList")
          },
          {
            path: "admin-list/:userId",
            redirect: { name: "adminList" }
          },
          {
            path: "admin-list/:userId/edit-roles",
            name: "editUserRoles",
            props: true,
            component: () =>
              import("@/components/Panel/AdminManagement/AdminList/EditRoles")
          }
        ]
      },
      {
        path: "account",
        name: "account",
        redirect: { name: "accountInfo" },
        component: () => import("@/views/Panel/Account"),
        children: [
          {
            path: "account-info",
            name: "accountInfo",
            component: () => import("@/components/Panel/Account/AccountInfo")
          },
          {
            path: "change-password",
            name: "accountChangePassword",
            component: () => import("@/components/Panel/Account/ChangePassword")
          }
        ]
      },
      {
        path: "*",
        name: "panel404",
        component: () => import("@/components/Panel/404")
      }
    ]
  },
  {
    path: "*",
    name: "404",
    redirect: { name: "loginForm" }
  }
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});

// router.beforeEach((to, from, next) => {
//   if (store.getters["userInfo/GET_USERINFO"].userStatus) {
//     AuthorizationCheck(to, from, next);
//   } else {
//     next();
//   }
// });
//
// const AuthorizationCheck = (to, from, next) => {
//   if (
//     test.includes(to.meta.authorization) ||
//     router.history.pending.matched[0].path === "/auth"
//   ) {
//     next();
//   } else {
//     if (!from.name || from.name === to.name) {
//       next({ name: "loginForm" });
//     } else {
//       next({ name: from.name, query: from.query });
//     }
//     Vue.$toast.error("Unfortunately, you are not authorized to access it.");
//   }
// };
//
// const test = ["300", "200", "220", "100", "100-1", "100-2"];

export default router;
